// FeedbackTypeSelection.js
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FaUser } from "react-icons/fa";


// ✅ ADD THIS HERE (top-level scope)
const roleLabelMap = {
  Self: "Self Assessment",
  Manager: "My Manager",
  Reportee: "My Direct Report",
  Peer: "My Peer"
};
const SmallModal = ({ title, message, onConfirm, onCancel, confirmText = "Logout", cancelText = "Cancel" }) => (
  <div style={{
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex",
    alignItems: "center", justifyContent: "center", zIndex: 2000
  }}>
    <div style={{ width: 420, background: "#fff", borderRadius: 8, padding: 20, boxShadow: "0 6px 30px rgba(0,0,0,0.3)" }}>
      <h3 style={{ marginTop: 0 }}>{title}</h3>
      <p style={{ marginBottom: 20 }}>{message}</p>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button onClick={onCancel} style={{ padding: "6px 10px" }}>{cancelText}</button>
        <button onClick={onConfirm} style={{ padding: "6px 10px", background: "#d9534f", color: "#fff", border: "none", borderRadius: 4 }}>{confirmText}</button>
      </div>
    </div>
  </div>
);

const FeedbackTypeSelection = () => {
  const [programs, setPrograms] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState("");
  const [feedbackType, setFeedbackType] = useState("");
  const [error, setError] = useState("");
  const [eligibility, setEligibility] = useState(null);
  
  const [selectedUser, setSelectedUser] = useState("");
  const [isChecking, setIsChecking] = useState(false);
  const [showAlreadyModal, setShowAlreadyModal] = useState(false);
  const [alreadyModalMessage, setAlreadyModalMessage] = useState("");
  const [submissionsMap, setSubmissionsMap] = useState({}); // { "<feedbackType>::<targetUserId>": true }
  const navigate = useNavigate();

  // login values
  const userId = localStorage.getItem("userId");
  const username = localStorage.getItem("username");
  const email = localStorage.getItem("email");
  const isParticipant = localStorage.getItem("isParticipant") === "true";
  const enrollmentRole = localStorage.getItem("enrollmentRole");

  useEffect(() => {
    window.history.pushState(null, "", window.location.href);
    const handlePopState = () => window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    if (!email) return;
    fetch(`/api/programs?email=${encodeURIComponent(email)}`)
      .then(res => res.json())
      .then(data => {
        if (!data || data.length === 0) {
          setError("No programs found for your account.");
          setPrograms([]);
          return;
        }
        setPrograms(data);
      })
      .catch(err => {
        console.error("Programs fetch error:", err);
        setError("Error fetching programs. Please try again later.");
      });
  }, [email]);

  // When program changes: reset state + early check if user already submitted for this program (any feedback)
  useEffect(() => {
    setFeedbackType("");
   
    setSelectedUser("");
    setEligibility(null);
    setSubmissionsMap({});

    if (!selectedProgram) return;

    // Program-level check: has user submitted anything for this program?
    (async () => {
      try {
        setIsChecking(true);
        const params = new URLSearchParams({ userId: userId || "", programId: selectedProgram });
        const resp = await fetch(`/api/check-program-submission?${params.toString()}`);
        if (resp.ok) {
          const json = await resp.json();
          if (json.success && json.alreadySubmitted) {
            // show modal with logout/cancel
            setAlreadyModalMessage("You have already submitted feedback for this program.");
            setShowAlreadyModal(true);
            setIsChecking(false);
            return;
          }
        } else {
          console.warn("Program submission check non-ok", resp.status);
        }
      } catch (err) {
        console.error("Program submission check error", err);
      } finally {
        setIsChecking(false);
      }

      // If not already fully submitted, fetch eligibility as before
      try {
        const url = `/api/eligibility/all?email=${encodeURIComponent(email)}&programId=${selectedProgram}`;
        const res = await fetch(url);
        if (res.ok) {
          const data = await res.json();
          setEligibility(data);

          // fetch existing user submissions (to grey-out options)
          // endpoint: GET /api/user-submissions?userId=...&programId=...
          const sresp = await fetch(`/api/user-submissions?userId=${userId}&programId=${selectedProgram}`);
          if (sresp.ok) {
            const sjson = await sresp.json();
            // expect sjson.rows = [{ target_user_id, feedback_type }, ...]
            const map = {};
            (sjson.rows || []).forEach(r => {
              const key = `${(r.feedback_type || "").toLowerCase()}::${r.target_user_id}`;
              map[key] = true;
            });
            setSubmissionsMap(map);
          }
        } else {
          console.error("eligibility fetch failed", res.status);
        }
      } catch (err) {
        console.error("eligibility error", err);
      }
    })();
  }, [selectedProgram, userId, email]);





  // populate options when feedbackType changes
  
  // helper to check one particular target selection
  const checkTargetSubmission = async (targetId, feedbackTypeToCheck) => {
    try {
      setIsChecking(true);
      const params = new URLSearchParams({
        userId: userId || "",
        targetUserId: targetId || "",
        programId: selectedProgram,
        feedbackType: feedbackTypeToCheck
      });
      const res = await fetch(`/api/check-submission?${params.toString()}`);
      if (res.ok) {
        const json = await res.json();
        if (json.success && json.alreadySubmitted) {
          setAlreadyModalMessage(`You have already submitted ${feedbackTypeToCheck} feedback for this person.`);
          setShowAlreadyModal(true);
          return true;
        }
      }
      return false;
    } catch (err) {
      console.error("check-target error", err);
      return false;
    } finally {
      setIsChecking(false);
    }
  };

  
  const handleProceed = async () => {
  setError("");

  if (!selectedProgram) {
    setError("Please select a program");
    return;
  }
if (!selectedUser && feedbackType !== "Self") {
  setError("Please select a participant");
  return;
}

  const [role, targetUserId] = selectedUser.split("::");

  const already = await checkTargetSubmission(targetUserId, role);
  if (already) return;

  localStorage.setItem("feedbackType", role);
  localStorage.setItem("targetUserId", targetUserId);
  localStorage.setItem("programId", selectedProgram);

  navigate("/surveyform");
};

  // helpers for UI: disabled option if submissionsMap says already done for that feedback type + target
  const optionDisabledKey = (ft, targetId) => {
    const key = `${(ft || "").toLowerCase()}::${targetId}`;
    return !!submissionsMap[key];
  };



const participantOptions = useMemo(() => {
  if (!eligibility) return [];

  const list = [];

  if (eligibility.self?.eligible) {
    list.push({
      id: userId,
      name: username,
      email,
      role: "Self",
    });
  }

  eligibility.manager?.managers?.forEach(u => {
    list.push({
      id: u.user_id,
      name: u.name,
      email: u.email,
      role: "Manager",
    });
  });

  eligibility.reportee?.reportees?.forEach(u => {
    list.push({
      id: u.user_id,
      name: u.name,
      email: u.email,
      role: "Reportee",
    });
  });

  eligibility.peer?.peers?.forEach(u => {
    list.push({
      id: u.user_id,
      name: u.peer_name || u.name,
      email: u.email,
      role: "Peer",
    });
  });

  return list;
}, [eligibility, userId, username, email]);






const selectedParticipant = participantOptions.find(
  p => `${p.role}::${p.id}` === selectedUser
);

const selectedRoleLabel = selectedParticipant?.role || "";
const selectedParticipantName = selectedParticipant?.name || "";

const roleChipStyle = {
  display: "inline-block",
  padding: "3px 10px",
  borderRadius: "14px",
  fontSize: "0.72rem",
  fontWeight: 600,
  letterSpacing: "0.3px",
  textTransform: "uppercase",
};

const roleColors = {
  Manager: { bg: "#2b6cb0", color: "#ffffff" },   // blue
  Peer: { bg: "#2f855a", color: "#ffffff" },      // green
  Reportee: { bg: "#805ad5", color: "#ffffff" },  // purple
  Self: { bg: "#718096", color: "#ffffff" },      // gray
};




return (
    <div className="page-wrapper">
   <div
  style={{
    position: "absolute",
    top: "20px",
    right: "20px",
    zIndex: 1000,
  }}
>
  <button
    type="button"
    className="logout-button"
    onClick={() => {
      localStorage.clear();
      sessionStorage.clear();
      navigate("/logout");
    }}
  >
    Logout
  </button>
</div>

  <div className="login-page">
        <div className="form-container">
          <img src="./UNextMABLogo06.png" alt="Logo" className="logo" />
          {/* <h2>Select Feedback Program & Type</h2> */}
 {/* {username && <p style={{ color: "white" }}>Welcome, {username} ({email})</p>} */}

{username && <p style={{ color: "white" }}>Welcome, {username} </p>}


          {error && <p className="error">{error}</p>}

          {/* Program selector */}
          <select value={selectedProgram}
            onChange={(e) => setSelectedProgram(e.target.value)}
            style={{ width: "100%", maxWidth: 190, padding: "0.4rem 0.6rem", margin: "0.5rem auto" }}>
            <option value="">-- Select Program --</option>
            {programs.map(p => <option key={p.program_id} value={p.program_id}>{p.program_name} ({p.feedback_type})</option>)}
          </select>


  
{participantOptions.length > 0  && (
  <select
    value={selectedUser}
 onChange={(e) => {
    const value = e.target.value;          // "Peer::123"
    if (!value) {
      setSelectedUser("");
      setFeedbackType("");
      return;
    }

    const [role, id] = value.split("::");

    setSelectedUser(value);                // ✅ KEEP FULL VALUE
    setFeedbackType(role);                 // auto role

    const selected = participantOptions.find(
      p => String(p.id) === id && p.role === role
    );

   localStorage.setItem("feedbackType", role);
    localStorage.setItem("targetUserId", id);
    localStorage.setItem("feedbackTargetName", selected?.name || ""); 
  }}
   style={{ width: "100%", maxWidth: 190, padding: "0.4rem 0.6rem", margin: "0.5rem auto" }}
    >
    <option value="">-- Select Participant --</option>
    {participantOptions.map(p => {
      const disabled = optionDisabledKey(p.role, p.id);
      return (
       <option
  key={`${p.role}-${p.id}`}
  value={`${p.role}::${p.id}`}
  disabled={disabled}
>
  {p.name}  {disabled ? " — done" : ""}
</option>

      );
    })}
  </select>
)}

          {/* Feedback Type */}
          {eligibility && (
          <select
  value={feedbackType}
  disabled
  style={{
    display: "block",
    width: "100%",
    maxWidth: 190,
    padding: "0.4rem 0.6rem",
    margin: "0.5rem auto",
    backgroundColor: "#e0e0e0"
  }}
>
 <option value="">
  {feedbackType
    ? roleLabelMap[feedbackType] || feedbackType
    : "--Feedback Context (auto)--"}
</option>
</select>



          )}



          <button onClick={handleProceed} className="submit-button" disabled={isChecking}>
            {isChecking ? "Checking..." : "Proceed"}
          </button>

{selectedRoleLabel && selectedParticipantName && (
  <div
    style={{
      marginTop: "8px",
      display: "inline-flex",
      alignItems: "center",
      gap: "10px",
      padding: "6px 12px",
      borderRadius: "18px",
      background: "rgba(255,255,255,0.08)",
    }}
  >
    <span
      style={{
        ...roleChipStyle,
        backgroundColor: roleColors[selectedRoleLabel]?.bg,
        color: roleColors[selectedRoleLabel]?.color,
      }}
    >
      Feedback For {selectedRoleLabel}
    </span>

    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        color: "#ffffff",
        fontWeight: 500
      }}
    >
      <FaUser size={14} />
      {selectedParticipantName}
    </span>
  </div>
)}

        </div>

        <footer className="footer"><p>© {new Date().getFullYear()} UNext Learning Pvt Ltd</p></footer>
      </div>

      {showAlreadyModal && (
        <SmallModal
          title="Feedback already submitted"
          message={alreadyModalMessage}
          confirmText="Logout"
          cancelText="Cancel"
          onConfirm={() => {
            // clear local and navigate to logout
            localStorage.clear();
            sessionStorage.clear();
            navigate("/logout");
          }}
          onCancel={() => {
            setShowAlreadyModal(false);
            // If program-level modal, user may want to choose another program -> reset selections
            // keep program selection so the user can possibly cancel and pick another - but you wanted cancel to let user pick other programs
            setSelectedProgram("");
            setFeedbackType("");
            setEligibility(null);
           
            setSelectedUser("");
            setSubmissionsMap({});
          }}
        />
      )}
    </div>
  );
};

export default FeedbackTypeSelection;
